/**
 * API client.
 *
 * The access token lives in a module-scoped variable — in memory only, never
 * in localStorage or sessionStorage. Web storage is readable by any script on
 * the page, so a single XSS foothold in any dependency turns into a stolen
 * token. Losing the token on refresh is the cost, and the HttpOnly refresh
 * cookie covers that: the app silently re-authenticates on load.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

let accessToken: string | null = null
let accessExpiresAt = 0

export function setAccessToken(token: string | null, expiresAt?: string) {
  accessToken = token
  accessExpiresAt = expiresAt ? Date.parse(expiresAt) : 0
}

export function hasSession(): boolean {
  return accessToken !== null
}

/** RFC 7807 problem document, as returned by the API for every failure. */
export interface Problem {
  type: string
  title: string
  status: number
  detail: string
  code: string
  errors?: { path: string; code: string; message: string }[]
  trace_id?: string
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail)
    this.name = 'ApiError'
  }

  /** Field errors keyed by JSON path, ready to hand to a form. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of this.problem.errors ?? []) out[e.path] = e.message
    return out
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Set by the retry path so a failed refresh cannot loop. */
  _retried?: boolean
}

/**
 * A single in-flight refresh, shared by every caller.
 *
 * Without this, a dashboard firing six parallel requests on a stale token
 * issues six concurrent refreshes. Since refresh tokens rotate and reuse
 * revokes the whole family, five of them would look like token theft to the
 * server and log the user out — a self-inflicted denial of service that only
 * shows up under real-world concurrency.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // sends the HttpOnly refresh cookie
      })
      if (!res.ok) {
        setAccessToken(null)
        return false
      }
      const data = (await res.json()) as { access_token: string; expires_at: string }
      setAccessToken(data.access_token, data.expires_at)
      return true
    } catch {
      setAccessToken(null)
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * The problem codes that mean "your bearer token is no good" — and only those.
 *
 * A 401 is not automatically a stale access token. `POST /auth/mfa/disable`
 * answers 401 with `mfa_invalid_code` when the code is wrong, and refreshing
 * and replaying that request submits the wrong code a second time: the server
 * counts two failed attempts for one thing the user did, so a five-attempt
 * lockout is reached in three tries. Retrying is only correct when the thing
 * that failed is the token, not the credential in the body.
 *
 * These three are emitted by the authenticator middleware and the token
 * parser, and nowhere else. Matching on them rather than excluding known
 * credential errors is deliberate: a new code that should have been listed
 * here produces a visible "please sign in again", while a new credential
 * error wrongly treated as retryable silently doubles someone's attempts.
 */
const TOKEN_FAILURE_CODES = new Set(['invalid_token', 'not_authenticated', 'invalid_authorization_header'])

function isTokenFailure(payload: unknown): boolean {
  const code = (payload as Problem | null)?.code
  // An unparseable 401 — a gateway or proxy answering before the API — has no
  // code. Treat it as a token failure so the silent refresh still covers the
  // case it was written for.
  return code === undefined || TOKEN_FAILURE_CODES.has(code)
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, _retried, headers, ...rest } = options

  // Refresh proactively when the token is within 30s of expiry, so the common
  // case is one request rather than a 401 followed by a retry.
  if (accessToken && accessExpiresAt && Date.now() > accessExpiresAt - 30_000 && !_retried) {
    await refreshAccessToken()
  }

  const finalHeaders = new Headers(headers)
  if (body !== undefined) finalHeaders.set('Content-Type', 'application/json')
  if (accessToken) finalHeaders.set('Authorization', `Bearer ${accessToken}`)

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  // A non-JSON body from a proxy must not throw before the error below gets a
  // chance to report the actual status.
  let payload: unknown = null
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    payload = null
  }

  // The body has to be read before deciding, because the decision depends on
  // the problem code inside it.
  if (res.status === 401 && !_retried && isTokenFailure(payload)) {
    if (await refreshAccessToken()) {
      return apiFetch<T>(path, { ...options, _retried: true })
    }
  }

  if (!res.ok) {
    // Every API failure is problem+json, so there is exactly one error shape
    // to handle. The fallback covers a proxy or gateway erroring before the
    // request ever reaches the API.
    const problem = (payload as Problem | null) ?? {
      type: 'about:blank',
      title: 'Error',
      status: res.status,
      detail: 'The request could not be completed.',
      code: 'unknown_error',
    }
    throw new ApiError(problem)
  }

  return payload as T
}

// --- auth --------------------------------------------------------------------

export interface LoginResponse {
  /** Absent when mfa_required is true — no session exists yet. */
  access_token?: string
  token_type?: string
  expires_at?: string

  /**
   * When true, the password was correct but authentication is NOT finished.
   * No session exists and no token was issued; the challenge must be exchanged
   * for one via verifyMfa. The flag is not advisory — the server issues
   * nothing until the second factor is proven.
   */
  mfa_required: boolean
  mfa_challenge_token?: string
  mfa_challenge_expires_at?: string
}

/**
 * Signs in to a specific organisation.
 *
 * The slug is required because `users` is RLS-protected server-side: the
 * tenant has to be established before the user table can be read at all.
 * It is remembered locally so returning users only type it once.
 */
export async function login(
  tenantSlug: string,
  email: string,
  password: string,
): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: { tenant_slug: tenantSlug, email, password },
  })
  // Only store a token if one was actually issued. An MFA response carries
  // none, and treating the challenge as a session would leave the app
  // believing it is signed in while every request 401s.
  if (res.access_token) {
    setAccessToken(res.access_token, res.expires_at)
  }
  localStorage.setItem('lg-tenant-slug', tenantSlug)
  return res
}

/** Exchanges an MFA challenge plus a code for a real session. */
export async function verifyMfa(challengeToken: string, code: string): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>('/api/v1/auth/mfa/verify', {
    method: 'POST',
    body: { mfa_challenge_token: challengeToken, code },
  })
  if (!res.access_token) {
    // Should be unreachable: a 200 from this endpoint always carries a token.
    // Throwing beats storing undefined and leaving the app apparently signed
    // in with no credential.
    throw new Error('Verification succeeded but no session was issued.')
  }
  setAccessToken(res.access_token, res.expires_at)
  return res
}

// --- MFA management ----------------------------------------------------------

export interface MfaStatus {
  status: 'disabled' | 'pending' | 'enabled'
  enrolled_at: string | null
  recovery_codes_remaining: number
  required_by_organisation: boolean
  /**
   * Set only after an administrator reset: the deadline for enrolling again
   * before the organisation's policy starts refusing this sign-in. Null in
   * every other case, including when the organisation does not require MFA.
   */
  grace_until: string | null
}

export function fetchMfaStatus(): Promise<MfaStatus> {
  return apiFetch<MfaStatus>('/api/v1/auth/mfa/status')
}

export interface MfaEnrolment {
  secret: string
  /** otpauth:// URI for the QR code. */
  provisioning_uri: string
}

export function beginMfaEnrolment(): Promise<MfaEnrolment> {
  return apiFetch<MfaEnrolment>('/api/v1/auth/mfa/enroll', { method: 'POST', body: {} })
}

export function confirmMfaEnrolment(code: string): Promise<{ recovery_codes: string[] }> {
  return apiFetch<{ recovery_codes: string[] }>('/api/v1/auth/mfa/enroll/confirm', {
    method: 'POST',
    body: { code },
  })
}

export function disableMfa(password: string, code: string): Promise<void> {
  return apiFetch<void>('/api/v1/auth/mfa/disable', {
    method: 'POST',
    body: { password, code },
  })
}

/**
 * Issues a fresh set of recovery codes and invalidates the previous one.
 *
 * A current code is required: without it a stolen session could mint a set of
 * long-lived credentials that outlive the session it was stolen from.
 */
export function regenerateRecoveryCodes(code: string): Promise<{ recovery_codes: string[] }> {
  return apiFetch<{ recovery_codes: string[] }>('/api/v1/auth/mfa/recovery-codes/regenerate', {
    method: 'POST',
    body: { code },
  })
}

/** The last organisation signed in to, for pre-filling the login form. */
export function rememberedTenantSlug(): string {
  return localStorage.getItem('lg-tenant-slug') ?? ''
}

export async function logout(): Promise<void> {
  try {
    await apiFetch<void>('/api/v1/auth/logout', { method: 'POST' })
  } finally {
    // Clear locally even if the call failed, so the UI never shows a signed-in
    // state the server has already ended.
    setAccessToken(null)
  }
}

export interface CurrentUser {
  user_id: string
  tenant_id: string
  email: string
  employee_id: string | null
  /** permission -> scope. Drives what the UI offers, never what it allows. */
  permissions: Record<string, string>
}

export function fetchMe(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/api/v1/auth/me')
}

/** Restores a session on page load using the HttpOnly refresh cookie. */
export async function restoreSession(): Promise<CurrentUser | null> {
  if (!(await refreshAccessToken())) return null
  try {
    return await fetchMe()
  } catch {
    return null
  }
}

// --- HR ----------------------------------------------------------------------

export interface Employee {
  id: string
  employee_number: string
  first_name: string
  last_name: string
  full_name: string
  work_email: string
  department_id: string | null
  position_id: string | null
  manager_id: string | null
  status: string
  employment_type: string
  hire_date: string
  probation_ends?: string
  termination_date?: string
  /** Absent — not null — when the caller lacks hr:compensation:read. */
  compensation?: {
    amount: string
    currency: string
    pay_period: string
    effective_from: string
  }
  /** Send back as If-Match on the next mutation. */
  version: number
  /** RFC 3339, always UTC. */
  updated_at: string
}

export interface Page<T> {
  items: T[]
  next_cursor?: string
  has_more: boolean
}

export interface ListEmployeesParams {
  cursor?: string
  limit?: number
  sort?: string
  status?: string[]
  q?: string
  department_id?: string
}

export function listEmployees(params: ListEmployeesParams = {}): Promise<Page<Employee>> {
  const qs = new URLSearchParams()
  if (params.cursor) qs.set('cursor', params.cursor)
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.sort) qs.set('sort', params.sort)
  if (params.status?.length) qs.set('status', params.status.join(','))
  if (params.q) qs.set('q', params.q)
  if (params.department_id) qs.set('department_id', params.department_id)

  const query = qs.toString()
  return apiFetch<Page<Employee>>(`/api/v1/hr/employees${query ? `?${query}` : ''}`)
}

export function getEmployee(id: string): Promise<Employee> {
  return apiFetch<Employee>(`/api/v1/hr/employees/${id}`)
}

export function terminateEmployee(
  id: string,
  version: number,
  input: { termination_date: string; reason: string },
): Promise<Employee> {
  return apiFetch<Employee>(`/api/v1/hr/employees/${id}/terminate`, {
    method: 'POST',
    // If-Match carries the version the client last read. The server returns
    // 409 if the record moved in the meantime, rather than silently
    // overwriting someone else's edit.
    headers: { 'If-Match': `"${version}"` },
    body: input,
  })
}
